/**
 * R6 UHF RFID Hand Scanner — Web Bluetooth (Nordic UART Service).
 *
 * Ported verbatim from gen5-pwa (src/utils/rfidScanner.ts), which itself came
 * from the rebound-smart Vue version. Connects to R6 scanners over BLE and
 * reads RFID EPC tags (UHF antenna) + barcodes (laser). React-free — the
 * Match/Manage Tags pages drive it via the callbacks below.
 *
 *   const scanner = new RfidScanner();
 *   scanner.onTagScanned = (epc) => { ... };
 *   scanner.onBleStatusChange = (connected, info) => { ... };
 *   await scanner.discoverAndConnect();
 *
 * Web Bluetooth is Chrome/Android/desktop-Chrome only (no iOS Safari) — gate
 * UI on `RfidScanner.isWebBluetoothAvailable` and fall back to the camera.
 */

// Nordic UART Service UUIDs (from the R6 SDK).
const NUS_SERVICE_UUID = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
const NUS_TX_CHAR_UUID = "6e400002-b5a3-f393-e0a9-e50e24dcca9e";
const NUS_RX_CHAR_UUID = "6e400003-b5a3-f393-e0a9-e50e24dcca9e";

const R6_CMD = {
  SCAN_BARCODE: new Uint8Array([0xa5, 0x5a, 0x00, 0x09, 0xe4, 0x02, 0xef, 0x0d, 0x0a]),
  STOP_BARCODE: new Uint8Array([0xa5, 0x5a, 0x00, 0x09, 0xe4, 0x01, 0xec, 0x0d, 0x0a]),
  GET_BATTERY: new Uint8Array([0xa5, 0x5a, 0x00, 0x08, 0x8c, 0x84, 0x0d, 0x0a]),
  GET_POWER: new Uint8Array([0xa5, 0x5a, 0x00, 0x08, 0x03, 0x0b, 0x0d, 0x0a]),
  INVENTORY_STOP: new Uint8Array([0xa5, 0x5a, 0x00, 0x09, 0xe0, 0x00, 0xe9, 0x0d, 0x0a]),
};

function makePkt(...payload: number[]): Uint8Array {
  const pkt: number[] = [0xa5, 0x5a];
  const total = 4 + payload.length + 1 + 2;
  pkt.push((total >> 8) & 0xff, total & 0xff);
  pkt.push(...payload);
  let xor = 0;
  for (let i = 2; i < pkt.length; i++) xor ^= pkt[i];
  pkt.push(xor, 0x0d, 0x0a);
  return new Uint8Array(pkt);
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface BleDeviceInfo {
  name: string;
  id: string;
  mac: string | null;
}

export class RfidScanner {
  // Web Bluetooth objects aren't in the default TS DOM lib; typed `any` (the
  // gen5-pwa convention) rather than pulling in @types/web-bluetooth.
  private bleDevice: any = null;
  private bleServer: any = null;
  private nusService: any = null;
  private txChar: any = null;
  private rxChar: any = null;
  bleConnected = false;

  private rxBuffer: number[] = [];
  private scanPending = false;
  private suppressAutoScan = false;
  private antennaScanning = false;

  onTagScanned: ((epc: string) => void) | null = null;
  /** Fires on 1D/2D barcode reads (trigger with the R6 in barcode mode).
   *  When null, barcode reads fall back to `onTagScanned` — preserves
   *  behavior for pages that don't distinguish. */
  onBarcodeScanned: ((text: string) => void) | null = null;
  onBleStatusChange: ((connected: boolean, info: BleDeviceInfo) => void) | null = null;
  onDeviceSelected: ((info: BleDeviceInfo) => void) | null = null;
  onBatteryLevel: ((level: number) => void) | null = null;

  static get isWebBluetoothAvailable(): boolean {
    const nav = navigator as any;
    return !!(nav.bluetooth && nav.bluetooth.requestDevice);
  }

  // Discovery intentionally uses `acceptAllDevices: true`. R6 units advertise
  // under several names depending on firmware/OS (sometimes "Nordic_BT_XXXX",
  // sometimes the raw 12-char uppercase MAC), and the OUI prefix isn't
  // consistent. Web Bluetooth filters don't support regex, so every narrow
  // filter we tried missed at least one device — showing all nearby BLE
  // devices and letting the user pick is the reliable path.
  async discoverAndConnect(_options?: { acceptAll?: boolean }): Promise<BleDeviceInfo> {
    if (!RfidScanner.isWebBluetoothAvailable) {
      throw new Error("Web Bluetooth not available. Use Chrome on Android or desktop.");
    }

    const nav = navigator as any;
    this.bleDevice = await nav.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [NUS_SERVICE_UUID, "device_information"],
    });

    const info: BleDeviceInfo = {
      name: this.bleDevice.name || "Unknown",
      id: this.bleDevice.id,
      mac: this.extractMacFromId(this.bleDevice.id),
    };

    if (this.onDeviceSelected) this.onDeviceSelected(info);

    this.bleDevice.addEventListener("gattserverdisconnected", () => {
      this.bleConnected = false;
      if (this.onBleStatusChange) this.onBleStatusChange(false, info);
    });

    this.bleServer = await this.bleDevice.gatt.connect();
    this.nusService = await this.bleServer.getPrimaryService(NUS_SERVICE_UUID);
    this.txChar = await this.nusService.getCharacteristic(NUS_TX_CHAR_UUID);
    this.rxChar = await this.nusService.getCharacteristic(NUS_RX_CHAR_UUID);

    await this.rxChar.startNotifications();
    this.rxChar.addEventListener("characteristicvaluechanged", (event: any) => {
      this.handleBleData(event.target.value);
    });

    this.bleConnected = true;
    if (this.onBleStatusChange) this.onBleStatusChange(true, info);

    await delay(500);
    return info;
  }

  disconnectBle(): void {
    if (this.bleDevice && this.bleDevice.gatt.connected) {
      this.bleDevice.gatt.disconnect();
    }
    this.bleConnected = false;
    this.bleDevice = null;
    this.bleServer = null;
    this.nusService = null;
    this.txChar = null;
    this.rxChar = null;
    this.rxBuffer = [];
  }

  async sendCommand(bytes: Uint8Array): Promise<void> {
    if (!this.txChar) throw new Error("BLE not connected");
    await this.txChar.writeValue(bytes);
  }

  async initUhfModule(power = 24, onProgress?: (msg: string) => void): Promise<void> {
    const txPower = Math.max(1, Math.min(30, Math.round(power)));
    if (onProgress) onProgress(`Initializing UHF module (${txPower} dBm)...`);
    this.suppressAutoScan = true;
    for (let cmd = 0x00; cmd <= 0x0f; cmd++) {
      try {
        await this.sendCommand(makePkt(cmd, txPower));
      } catch {
        /* ignore */
      }
      await delay(250);
    }
    this.suppressAutoScan = false;
    if (onProgress) onProgress("UHF module initialized.");
  }

  async triggerAntennaScan(power = 24): Promise<void> {
    if (this.antennaScanning) return;
    this.antennaScanning = true;
    const txPower = Math.max(1, Math.min(30, Math.round(power)));

    for (let i = 0; i < 8; i++) {
      try {
        await this.sendCommand(makePkt(0x80, txPower));
      } catch {
        /* ignore */
      }
      await delay(400);
    }

    try {
      await this.sendCommand(makePkt(0xe0, txPower));
    } catch {
      /* ignore */
    }
    await delay(3000);
    try {
      await this.sendCommand(R6_CMD.INVENTORY_STOP);
    } catch {
      /* ignore */
    }

    this.antennaScanning = false;
  }

  async triggerScan(): Promise<void> {
    await this.sendCommand(R6_CMD.SCAN_BARCODE);
  }

  async getBattery(): Promise<void> {
    await this.sendCommand(R6_CMD.GET_BATTERY);
  }

  private handleBleData(dataView: DataView): void {
    const bytes = new Uint8Array(dataView.buffer);
    for (let i = 0; i < bytes.length; i++) this.rxBuffer.push(bytes[i]);
    this.processRxBuffer();
  }

  private processRxBuffer(): void {
    while (this.rxBuffer.length >= 6) {
      const headerIdx = this.findHeader();
      if (headerIdx < 0) {
        this.rxBuffer = [];
        return;
      }
      if (headerIdx > 0) this.rxBuffer.splice(0, headerIdx);
      if (this.rxBuffer.length < 4) return;

      const packetLen = (this.rxBuffer[2] << 8) | this.rxBuffer[3];

      if (packetLen >= 6 && packetLen <= 1024 && this.rxBuffer.length >= packetLen) {
        const packet = this.rxBuffer.splice(0, packetLen);
        if (packet[packet.length - 2] === 0x0d && packet[packet.length - 1] === 0x0a) {
          this.handlePacket(packet);
        }
        continue;
      }

      let tailIdx = -1;
      for (let i = 4; i < this.rxBuffer.length - 1; i++) {
        if (this.rxBuffer[i] === 0x0d && this.rxBuffer[i + 1] === 0x0a) {
          tailIdx = i;
          break;
        }
      }
      if (tailIdx < 0) return;

      const packet = this.rxBuffer.splice(0, tailIdx + 2);
      this.handlePacket(packet);
    }
  }

  private findHeader(): number {
    for (let i = 0; i < this.rxBuffer.length - 1; i++) {
      if (this.rxBuffer[i] === 0xa5 && this.rxBuffer[i + 1] === 0x5a) return i;
    }
    return -1;
  }

  private emitTag(epc: string): void {
    if (this.onTagScanned) this.onTagScanned(epc);
  }

  private handlePacket(packet: number[]): void {
    const o = 4;
    if (o >= packet.length - 2) return;
    const cmd = packet[o];

    // Barcode/QR response
    if (cmd === 0xe4 || cmd === 0xe5 || cmd === 0xe6) {
      const sub = packet[o + 1];
      if (sub === 0x02 && packet.length > o + 4) {
        const dataBytes = packet.slice(o + 2, packet.length - 3);
        if (dataBytes.length > 0) {
          const text = new TextDecoder().decode(new Uint8Array(dataBytes)).trim();
          if (text.length > 0) {
            // Prefer the barcode-specific handler; fall back to the tag
            // handler for pages that don't distinguish.
            if (this.onBarcodeScanned) this.onBarcodeScanned(text);
            else this.emitTag(text);
          }
        }
      }
      if (sub === 0x01 && (cmd === 0xe5 || cmd === 0xe6) && !this.scanPending) {
        this.scanPending = true;
        this.sendCommand(R6_CMD.SCAN_BARCODE)
          .then(() => {
            setTimeout(() => {
              this.scanPending = false;
            }, 1000);
          })
          .catch(() => {
            this.scanPending = false;
          });
      }
      return;
    }

    // Single tag read response
    if (cmd === 0x81) {
      const dataStart = o + 1;
      const dataEnd = packet.length - 3;
      const dataLen = dataEnd - dataStart;
      if (dataLen >= 6) {
        const pcHi = packet[dataStart];
        const epcWordCount = (pcHi >> 3) & 0x1f;
        const epcByteCount = epcWordCount * 2;
        const epcLen =
          epcByteCount > 0 && epcByteCount + 2 <= dataLen ? epcByteCount : Math.max(0, dataLen - 4);
        if (epcLen > 0) {
          const epcBytes = packet.slice(dataStart + 2, dataStart + 2 + epcLen);
          let epcStart = 0;
          while (epcStart < epcBytes.length && epcBytes[epcStart] === 0x00) epcStart++;
          const trimmedEpc = epcBytes.slice(epcStart);
          if (trimmedEpc.length > 0) {
            const hexStr = Array.from(trimmedEpc)
              .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
              .join("");
            this.emitTag(hexStr);
          }
        }
      }
      return;
    }

    // RFID inventory response
    if (cmd === 0xe1) {
      const tagCount = packet[o + 3];
      if (tagCount > 0) {
        let pos = o + 4;
        const endPos = packet.length - 3;
        for (let t = 0; t < tagCount && pos < endPos; t++) {
          const recLen = packet[pos];
          if (recLen < 4 || pos + 1 + recLen > endPos) break;
          pos++;

          const pcHi = packet[pos];
          const epcWordCount = (pcHi >> 3) & 0x1f;
          const epcByteCount = epcWordCount * 2;
          const epcLen =
            epcByteCount > 0 && epcByteCount + 4 <= recLen ? epcByteCount : recLen - 4;
          if (epcLen <= 0) {
            pos += recLen;
            continue;
          }

          const epcBytes = packet.slice(pos + 2, pos + 2 + epcLen);
          let epcStart = 0;
          while (epcStart < epcBytes.length && epcBytes[epcStart] === 0x00) epcStart++;
          const trimmedEpc = epcBytes.slice(epcStart);

          const asciiText = new TextDecoder().decode(new Uint8Array(trimmedEpc));
          // eslint-disable-next-line no-control-regex
          const cleaned = asciiText.replace(/[^\x20-\x7E]/g, "").trim();

          if (cleaned.length > 3) {
            this.emitTag(cleaned);
          } else if (trimmedEpc.length > 0) {
            const hexStr = Array.from(trimmedEpc)
              .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
              .join("");
            this.emitTag(hexStr);
          }

          pos += recLen;
        }
      }
      return;
    }

    // Battery response
    if (cmd === 0x8c || cmd === 0x8d) {
      const batteryByte = packet[o + 1];
      if (this.onBatteryLevel) this.onBatteryLevel(batteryByte);
      return;
    }

    // Power response (ignored)
    if (cmd === 0x04 || cmd === 0x03) return;

    // Auto-trigger QR on unknown short packets (trigger button press).
    const knownCmds = [
      0xe0, 0xe1, 0xe4, 0xe5, 0xe6, 0x23, 0x81, 0x8c, 0x8d, 0x22, 0x28, 0x77, 0x78, 0x76, 0x03,
      0x04,
    ];
    if (
      packet.length <= 14 &&
      !this.scanPending &&
      !this.suppressAutoScan &&
      !knownCmds.includes(cmd)
    ) {
      this.scanPending = true;
      this.sendCommand(R6_CMD.SCAN_BARCODE)
        .then(() => {
          setTimeout(() => {
            this.scanPending = false;
          }, 1000);
        })
        .catch(() => {
          this.scanPending = false;
        });
    }
  }

  private extractMacFromId(deviceId: string): string | null {
    const macRegex =
      /([0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2})/;
    const match = deviceId.match(macRegex);
    return match ? match[1].toUpperCase() : null;
  }
}
